import { useAtomValue } from 'jotai';
import { ForwardedRef, forwardRef, useState, memo, ImgHTMLAttributes } from 'react';
import { getUrlWithToken } from '../../../utils/getMediaURLWithToken';
import { useGetToken } from '../../../hooks/useGetToken';

interface Props {
  readonly placeholderSrc?: string;
  readonly imgProps?: Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'>;
  readonly src: string;
}

const ProgressiveImage = memo(
  forwardRef(({ placeholderSrc, src, imgProps = {} }: Props, ref: ForwardedRef<HTMLImageElement>) => {
    const [currentSrc, setCurrentSrc] = useState(src);
    const [retryCount, setRetryCount] = useState(0);
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const MAX_RETRIES = 4;
    const RETRY_DELAY = 2000;
    const token = useGetToken();

    const handleImageError = () => {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => {
          setRetryCount(retryCount + 1);
          setCurrentSrc((prevSrc) => {
            const url = new URL(prevSrc);
            url.searchParams.set('retry', (retryCount + 1).toString());
            url.searchParams.set('token', token ?? '');
            return url.toString();
          });
        }, RETRY_DELAY);
      } else {
        setCurrentSrc(getUrlWithToken(placeholderSrc ?? '', token ?? ''));
      }
    };

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setIsImageLoaded(true);
      imgProps.onLoad?.(e);
    };

    return (
      <>
        <img
          alt=""
          ref={ref}
          {...(imgProps || {})}
          style={{
            ...imgProps.style,
            height: isImageLoaded ? '0px' : imgProps.style?.height,
            width: isImageLoaded ? '0px' : imgProps.style?.width,
          }}
          src={getUrlWithToken(placeholderSrc ?? '', token ?? '')}
        />
        <img
          alt=""
          ref={ref}
          {...(imgProps || {})}
          style={{
            ...imgProps.style,
            height: !isImageLoaded ? '0px' : imgProps.style?.height,
            width: !isImageLoaded ? '0px' : imgProps.style?.width,
          }}
          src={getUrlWithToken(currentSrc, token ?? '')}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </>
    );
  }),
  (prevProps: Props, nextProps: Props) => {
    return (
      prevProps.src === nextProps.src &&
      prevProps.placeholderSrc === nextProps.placeholderSrc &&
      Object.keys(prevProps.imgProps?.style ?? {}) === Object.keys(nextProps.imgProps?.style ?? {})
    );
  }
);

export default ProgressiveImage;
